import axios from 'axios';
import { 
  Guest, 
  Task, 
  CreateGuestRequest, 
  CreateTaskRequest, 
  UpdateTaskRequest,
  User,
  Hotel,
  LoginRequest,
  LoginResponse,
  CreateUserRequest,
  UpdateUserRequest,
  UpdateHotelRequest,
  OnboardingData
} from '../types';
import { config } from '../config';

const API_BASE_URL = config.API_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    // Treat both 401 (unauthorized) and 403 (invalid/expired token) as auth failures
    if (status === 401 || status === 403) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('hotel');
      // Force a hard redirect so App re-mounts and shows Login
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Guest API
export const guestAPI = {
  getAllGuests: async (): Promise<Guest[]> => {
    const response = await api.get('/guests');
    return response.data;
  },

  createGuest: async (guest: CreateGuestRequest): Promise<Guest> => {
    const response = await api.post('/guests', guest);
    return response.data;
  },

  updateGuestStatus: async (id: string, status: 'checked_in' | 'checked_out'): Promise<Guest> => {
    const response = await api.patch(`/guests/${id}/status`, { status });
    return response.data;
  },

  updateGuest: async (
    id: string,
    updates: Partial<{
      name: string;
      room_number: string;
      whatsapp_number: string;
      marketing_opt_in: boolean;
    }>
  ): Promise<Guest> => {
    const response = await api.patch(`/guests/${id}`, updates);
    return response.data;
  },

  deleteGuest: async (id: string): Promise<void> => {
    await api.delete(`/guests/${id}`);
  },
};

// Task API
export const taskAPI = {
  getAllTasks: async (): Promise<Task[]> => {
    const response = await api.get('/tasks');
    return response.data;
  },

  createTask: async (task: CreateTaskRequest): Promise<Task> => {
    const response = await api.post('/tasks', task);
    return response.data;
  },

  updateTaskStatus: async (id: string, updates: UpdateTaskRequest): Promise<Task> => {
    const response = await api.patch(`/tasks/${id}`, updates);
    return response.data;
  },

  deleteTask: async (id: string): Promise<void> => {
    await api.delete(`/tasks/${id}`);
  },
};

// Dashboard Stats API
export const dashboardAPI = {
  getStats: async () => {
    const response = await api.get('/dashboard/stats');
    return response.data;
  },
  getOverview: async () => {
    const response = await api.get('/dashboard/overview');
    return response.data;
  },
};

// Feedback API
export const feedbackAPI = {
  submit: async (payload: {
    guest_id?: string;
    guest_name?: string;
    room_number?: string;
    rating: 'positive' | 'neutral' | 'negative';
    comment?: string;
  }) => {
    const response = await api.post('/feedback', payload);
    return response.data;
  },
  list: async (guestId?: string) => {
    const response = await api.get('/feedback', {
      params: guestId ? { guest_id: guestId } : {},
    });
    return response.data;
  },
};

// Conversations / Chat API
export const conversationAPI = {
  list: async (guestId?: string) => {
    const response = await api.get('/conversations', {
      params: guestId ? { guest_id: guestId } : {},
    });
    return response.data;
  },
  listMessages: async (id: string) => {
    const response = await api.get(`/conversations/${id}/messages`);
    return response.data;
  },
  sendAgentMessage: async (id: string, content: string) => {
    const response = await api.post(`/conversations/${id}/messages`, { content });
    return response.data;
  },
  setTakeover: async (id: string, agent_takeover: boolean) => {
    const response = await api.patch(`/conversations/${id}/takeover`, { agent_takeover });
    return response.data;
  },
};

// Auth API
export const authAPI = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  onboard: async (
    data: OnboardingData
  ): Promise<
    LoginResponse & { message: string; hotelId: string; userId: string }
  > => {
    const response = await api.post('/auth/onboard', data);
    return response.data;
  },

  getUsers: async (): Promise<User[]> => {
    const response = await api.get('/auth/users');
    return response.data;
  },

  createUser: async (user: CreateUserRequest): Promise<User> => {
    const response = await api.post('/auth/users', user);
    return response.data;
  },

  updateUser: async (id: string, updates: UpdateUserRequest): Promise<User> => {
    const response = await api.patch(`/auth/users/${id}`, updates);
    return response.data;
  },

  deleteUser: async (id: string): Promise<void> => {
    await api.delete(`/auth/users/${id}`);
  },

  updateHotel: async (updates: UpdateHotelRequest): Promise<Hotel> => {
    const response = await api.patch('/auth/hotel', updates);
    return response.data;
  },
};

// Campaigns & segments (WhatsApp CRM)
export const campaignAPI = {
  getSuggestions: async (): Promise<{
    campaigns: Array<{
      id: string;
      title: string;
      body: string;
      category: string;
      audience_type: 'all_checked_in' | 'segment' | 'manual';
      segment_hint?: string;
      rationale: string;
    }>;
    segments: Array<{
      id: string;
      name: string;
      definition: { room_prefix?: string; room_numbers?: string[] };
      rationale: string;
    }>;
    context: {
      checked_in_marketing: number;
      segment_count: number;
      campaign_draft_count: number;
    };
  }> => {
    const response = await api.get('/campaigns/suggestions');
    return response.data;
  },
  list: async () => {
    const response = await api.get('/campaigns');
    return response.data;
  },
  get: async (id: string) => {
    const response = await api.get(`/campaigns/${id}`);
    return response.data;
  },
  draftAudience: async (id: string): Promise<{
    guests: { id: string; name: string; room_number: string }[];
  }> => {
    const response = await api.get(`/campaigns/${id}/draft-audience`);
    return response.data;
  },
  create: async (body: {
    title: string;
    body: string;
    audience_type: 'all_checked_in' | 'segment' | 'manual';
    segment_id?: string | null;
    category?: string | null;
    attribution_window_hours?: number;
    manual_guest_ids?: string[];
  }) => {
    const response = await api.post('/campaigns', body);
    return response.data;
  },
  update: async (id: string, body: Record<string, unknown>) => {
    const response = await api.patch(`/campaigns/${id}`, body);
    return response.data;
  },
  publish: async (id: string, body?: { scheduled_at?: string | null }) => {
    const response = await api.post(`/campaigns/${id}/publish`, body ?? {});
    return response.data;
  },
  recipients: async (id: string) => {
    const response = await api.get(`/campaigns/${id}/recipients`);
    return response.data;
  },
  insights: async (id: string) => {
    const response = await api.get(`/campaigns/${id}/insights`);
    return response.data;
  },
  listSegments: async () => {
    const response = await api.get('/campaigns/segments');
    return response.data;
  },
  createSegment: async (name: string, definition: Record<string, unknown>) => {
    const response = await api.post('/campaigns/segments', { name, definition });
    return response.data;
  },
  previewSegment: async (definition: Record<string, unknown>) => {
    const response = await api.post('/campaigns/segments/preview', { definition });
    return response.data;
  },
};

async function waitForPdfIngestJob(
  sessionId: string,
  options?: { intervalMs?: number; maxWaitMs?: number }
): Promise<Record<string, unknown>> {
  const intervalMs = options?.intervalMs ?? 1500;
  const maxWaitMs = options?.maxWaitMs ?? 20 * 60 * 1000;
  const start = Date.now();
  while (Date.now() - start < maxWaitMs) {
    const { data } = await api.get(
      `/pdfs/job-status/${encodeURIComponent(sessionId)}`
    );
    if (data.status === 'complete' || data.status === 'complete_refresh') {
      return data;
    }
    if (data.status === 'error') {
      throw new Error(data.error || 'PDF ingest failed');
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error(
    'PDF processing timed out. Check server logs; you can retry upload or refresh.'
  );
}

// PDF Upload API (server queues parse+embed after 202; we poll job-status)
export const pdfAPI = {
  uploadPdf: async (
    file: File,
    category: string
  ): Promise<{ message?: string; sessionId?: string; accepted?: boolean; completed?: boolean; result?: Record<string, unknown> }> => {
    const sessionId = `upload_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    const formData = new FormData();
    formData.append('pdf', file);
    formData.append('category', category);
    formData.append('sessionId', sessionId);

    const response = await api.post('/pdfs/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0,
    });

    const d = response.data;
    if (response.status === 202 || d.accepted) {
      const result = await waitForPdfIngestJob(sessionId);
      return { ...d, completed: true, result };
    }
    return response.data;
  },

  getPdfs: async (): Promise<any[]> => {
    const response = await api.get('/pdfs');
    return response.data;
  },

  deletePdf: async (filename: string): Promise<void> => {
    await api.delete(`/pdfs/${encodeURIComponent(filename)}`);
  },
};

export const knowledgeAPI = {
  add: async (payload: { title?: string; content: string }): Promise<{
    success: boolean;
    chunksAdded: number;
  }> => {
    const response = await api.post('/knowledge', payload);
    return response.data;
  },
  list: async (limit?: number): Promise<{
    items: Array<{
      id: string;
      source_type: string;
      source_ref: string | null;
      preview: string;
      created_at: string;
    }>;
  }> => {
    const response = await api.get('/knowledge', {
      params: limit ? { limit } : {},
    });
    return response.data;
  },
};
